import { NavLink, useNavigate } from "react-router-dom";

import { clientName } from "../api/types";
import type { Client } from "../api/types";
import Avatar from "./Avatar";
import BrandMark from "./BrandMark";
import { isReady } from "../lib/flags";
import Button from "./Button";
import ThemeToggle from "./ThemeToggle";
import "./Sidebar.css";

/** The plus that adds a client, now the whole control: 1v1 asked for the icon
 *  by the Clients heading rather than a labelled button in the footer. */
const Plus = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const Cog = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M6.4 2.2 6.1 4A5 5 0 0 0 5 4.6L3.2 4l-1.4 2.4 1.5 1.1a5 5 0 0 0 0 1L1.8 9.6 3.2 12l1.8-.6a5 5 0 0 0 1.1.6l.3 1.8h2.8L9.5 12a5 5 0 0 0 1.1-.6l1.8.6 1.4-2.4-1.5-1.1a5 5 0 0 0 0-1l1.5-1.1L12.4 4l-1.8.6A5 5 0 0 0 9.5 4L9.2 2.2H6.4Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="8" r="1.7" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

/** The rail's expand and collapse control, drawn as a window with the left
 *  pane filled when the sidebar is open. A chevron said "go that way"; this
 *  says which piece of the layout the button is about. */
export function SidebarLayoutIcon({ open }: { open: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="1.75"
        y="1.75"
        width="12.5"
        height="12.5"
        rx="2.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      {open ? (
        <path
          d="M1.75 4a2.25 2.25 0 0 1 2.25-2.25H6.5v12.5H4A2.25 2.25 0 0 1 1.75 12V4Z"
          fill="currentColor"
        />
      ) : (
        <path d="M6.5 1.75v12.5" stroke="currentColor" strokeWidth="1.5" />
      )}
    </svg>
  );
}

/** Figma: sidebar 240 wide, fill slate-900, padding 24, gap 38.
 *  Client items 192 wide, active is a filled blue pill at radius 10. */
export default function Sidebar({
  clients,
  clientsFailed = false,
  onRetryClients,
  collapsed = false,
  moving = false,
  onToggleCollapsed,
  mobileOpen = false,
  onCloseMobile,
}: {
  clients: Client[];
  clientsFailed?: boolean;
  onRetryClients?: () => void;
  collapsed?: boolean;
  /** Set while the rail is opening or closing, so its contents fade out
   *  while it moves. The shell runs the sequence. */
  moving?: boolean;
  onToggleCollapsed?: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}) {
  const navigate = useNavigate();

  return (
    <aside
      className={[
        "sidebar",
        collapsed ? "sidebar--collapsed" : "",
        mobileOpen ? "sidebar--mobile-open" : "",
        moving ? "sidebar--moving" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="sidebar__top">
        <NavLink
          to="/"
          className="sidebar__logo"
          aria-label="CastFlow home"
          onClick={onCloseMobile}
        >
          <span className="sidebar__mark" aria-hidden="true">
            <BrandMark size={20} />
          </span>
          <span className="sidebar__word">CastFlow</span>
        </NavLink>

        {/* Collapse belongs to the rail, not to the person, so it sits with
            the rail's own name. It used to be the last row of the footer,
            which is where the account now goes. */}
        {onToggleCollapsed && (
          <button
            type="button"
            className="sidebar__collapse"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            <SidebarLayoutIcon open={!collapsed} />
          </button>
        )}
      </div>

      <div className="sidebar__section">
        {/* Add client is the plus beside the heading now, not a labelled
            button in the footer. Gated like the old one: when the screen is
            not ready it draws as designed but does not route anywhere. */}
        <div className="sidebar__sectionhead">
          <span className="sidebar__label">CLIENTS</span>
          {isReady("add-client") ? (
            <NavLink
              to="/clients/new"
              className="sidebar__add"
              aria-label="Add client"
              title="Add client"
              onClick={onCloseMobile}
            >
              <Plus />
            </NavLink>
          ) : (
            <span className="sidebar__add sidebar__add--off" aria-hidden="true">
              <Plus />
            </span>
          )}
        </div>
        <nav className="sidebar__clients">
          {/* An empty list and a list that failed to load look identical
              once they are both nothing, and they mean opposite things. A
              manager who sees no clients where there were three will think
              they are gone. */}
          {clientsFailed && (
            <div className="sidebar__failed">
              <span>Could not load your clients.</span>
              {onRetryClients && (
                <button type="button" onClick={onRetryClients}>
                  Try again
                </button>
              )}
            </div>
          )}
          {clients.map((c) => {
            const body = (
              <>
                <Avatar name={clientName(c)} src={c.avatar_url} size={24} />
                <span className="client-item__name">{clientName(c)}</span>
                {/* Editing lives on the client it acts on. It used to be a top
                    bar button that applied to whichever client a separate
                    dropdown was pointing at, which meant reading two places
                    to know what it would do. */}
                {isReady("matches") && (
                  <span
                    className="client-item__setup"
                    role="button"
                    tabIndex={0}
                    aria-label={`Edit ${clientName(c)}`}
                    title={`Edit ${clientName(c)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/clients/${c.id}/edit`);
                      onCloseMobile?.();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/clients/${c.id}/edit`);
                        onCloseMobile?.();
                      }
                    }}
                  >
                    <Cog />
                  </span>
                )}
              </>
            );

            // Gated: the item is part of the dashboard design, so it renders
            // as designed and highlights the current client, it just does not
            // navigate to a screen that is not ready.
            // Highlighted only on that client's own screen. The dashboard
            // used to highlight one too, which made sense while a dropdown
            // up in the bar could change which. That dropdown is gone, and
            // nothing on the dashboard is scoped to a client anyway, so the
            // highlight was pointing at whichever client happened to load
            // first and could not be moved off it.
            return !isReady("matches") ? (
              <div key={c.id} className="client-item">
                {body}
              </div>
            ) : (
              <NavLink
                key={c.id}
                to={`/clients/${c.id}`}
                className={({ isActive }) =>
                  `client-item ${isActive ? "client-item--active" : ""}`
                }
                onClick={onCloseMobile}
              >
                {body}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="sidebar__foot">
        {isReady("uploads") && (
          <NavLink to="/uploads" onClick={onCloseMobile}>
            <Button
              variant="sidebar-ghost"
              icon={
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M8 10V3M5 6l3-3 3 3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 10.5v1A1.5 1.5 0 0 0 4.5 13h7a1.5 1.5 0 0 0 1.5-1.5v-1"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              }
            >
              <span className="sidebar__addword">Upload grid</span>
            </Button>
          </NavLink>
        )}
        <div className="sidebar__settings">
          <div className="sidebar__theme">
            <span className="sidebar__themeicon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.5 9.6A5.8 5.8 0 0 1 6.4 2.5a5.8 5.8 0 1 0 7.1 7.1Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="sidebar__themelabel">Dark mode</span>
            <ThemeToggle variant="switch" />
          </div>
        </div>
      </div>
    </aside>
  );
}
